import { useAuth } from "@/contexts/AuthContext";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarDays, Package, MessageSquare, Plus, LogOut, Star, Settings, ShoppingCart, Pencil, Clock, User, Building2 } from "lucide-react";
import { PurchaseRequestsTab } from "@/components/PurchaseRequestsTab";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const statusLabels: Record<string, string> = {
  requested: "Förfrågad",
  accepted: "Accepterad",
  declined: "Nekad",
  scheduled: "Schemalagd",
  in_progress: "Pågår",
  completed: "Slutförd",
  confirmed: "Bekräftad",
  paid_out: "Utbetald",
};

const statusColors: Record<string, string> = {
  requested: "bg-amber-100 text-amber-800",
  accepted: "bg-primary/10 text-primary",
  declined: "bg-destructive/10 text-destructive",
  scheduled: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-primary/10 text-primary",
  confirmed: "bg-primary/20 text-primary",
  paid_out: "bg-primary/30 text-primary",
};

export default function Dashboard() {
  const { user, profile, roles, signOut, hasRole } = useAuth();
  const [bookings, setBookings] = useState<any[]>([]);
  const [listings, setListings] = useState<any[]>([]);
  const [providerProfile, setProviderProfile] = useState<any>(null);
  const [providerBookings, setProviderBookings] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;
    
    // Fetch customer bookings
    supabase
      .from("bookings")
      .select("*, services(title, service_type), provider_profiles:provider_id(business_name)")
      .eq("customer_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setBookings(data); });

    // Fetch user listings
    supabase
      .from("listings")
      .select("*")
      .eq("seller_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setListings(data); });

    // Fetch provider data only for company accounts
    if (profile?.account_type === "company") {
      supabase
        .from("provider_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle()
        .then(({ data }) => {
          setProviderProfile(data);
          if (data) {
            supabase
              .from("bookings")
              .select("*, services(title, service_type), profiles:customer_id(full_name)")
              .eq("provider_id", data.id)
              .order("created_at", { ascending: false })
              .then(({ data: bd }) => { if (bd) setProviderBookings(bd); });
          }
        });
    }
  }, [user, roles, profile?.account_type]);

  const handleBookingAction = async (bookingId: string, newStatus: string) => {
    const { error } = await supabase
      .from("bookings")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", bookingId);
    if (error) {
      toast.error("Kunde inte uppdatera bokning");
    } else {
      toast.success("Bokning uppdaterad!");
      // Refresh
      setProviderBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
    }
  };

  const accountTypeLabels: Record<string, string> = {
    personal: "Privatkonto",
    company: "Företagskonto",
    admin: "Administratör",
  };
  const isCompany = profile?.account_type === "company";
  const companyPendingApproval = isCompany && !profile?.admin_approved;

  const defaultTab = isCompany ? "provider" : "bookings";
  const TAB_STORAGE_KEY = "dashboard-active-tab-v1";
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return sessionStorage.getItem(TAB_STORAGE_KEY) || defaultTab;
    } catch {
      return defaultTab;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, activeTab);
    } catch {
      // ignore
    }
  }, [activeTab]);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container flex-1 py-8">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Välkommen, {profile?.full_name || "användare"}</h1>
            <div className="mt-1 flex gap-2">
              {profile?.account_type && (
                <Badge variant="secondary" className="text-xs">
                  {profile.account_type === "company" ? <Building2 className="mr-1 h-3 w-3" /> : <User className="mr-1 h-3 w-3" />}
                  {accountTypeLabels[profile.account_type]}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/redigera-profil"><Pencil className="mr-2 h-4 w-4" /> Redigera profil</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/saljare/${profile?.username || user?.id}`}>Se offentlig profil</Link>
            </Button>
            {hasRole("admin") && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin"><Settings className="mr-2 h-4 w-4" /> Admin</Link>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={signOut}>
              <LogOut className="mr-2 h-4 w-4" /> Logga ut
            </Button>
          </div>
        </div>

        {companyPendingApproval && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            <Clock className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="text-sm">Ditt företagskonto väntar på granskning. Vi meddelar dig när det har verifierats.</p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 w-full overflow-x-auto overflow-y-hidden flex justify-start flex-nowrap no-scrollbar">
            <TabsTrigger value="bookings">
              <CalendarDays className="mr-2 h-4 w-4" /> Mina bokningar
            </TabsTrigger>
            <TabsTrigger value="listings">
              <Package className="mr-2 h-4 w-4" /> Mina annonser
            </TabsTrigger>
            {isCompany && (
              <TabsTrigger value="provider">
                <Star className="mr-2 h-4 w-4" /> Leverantör
              </TabsTrigger>
            )}
            <TabsTrigger value="purchases">
              <ShoppingCart className="mr-2 h-4 w-4" /> Prisförslag
            </TabsTrigger>
            <TabsTrigger value="messages">
              <MessageSquare className="mr-2 h-4 w-4" /> Meddelanden
            </TabsTrigger>
          </TabsList>

          {/* Customer bookings */}
          <TabsContent value="bookings">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-sans text-lg font-semibold">Bokningar</h2>
            </div>
            {bookings.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-8 text-center">
                <p className="text-muted-foreground">Du har inga bokningar ännu.</p>
                <Button className="mt-4" asChild><Link to="/skogstjanster">Boka en tjänst</Link></Button>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
                    <div>
                      <p className="font-medium">{(b as any).services?.title || "Tjänst"}</p>
                      <p className="text-sm text-muted-foreground">
                        {(b as any).provider_profiles?.business_name} • {b.scheduled_date || "Inget datum"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[b.status] || "bg-muted text-muted-foreground"}`}>
                        {statusLabels[b.status] || b.status}
                      </span>
                      {b.status === "completed" && (
                        <Button size="sm" onClick={() => handleBookingAction(b.id, "confirmed")}>Bekräfta</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Seller listings */}
          <TabsContent value="listings">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-sans text-lg font-semibold">Mina annonser</h2>
              <Button size="sm" asChild><Link to="/skapa-annons"><Plus className="mr-2 h-4 w-4" /> Ny annons</Link></Button>
            </div>
            {listings.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-8 text-center">
                <p className="text-muted-foreground">Du har inga annonser ännu.</p>
                <Button className="mt-4" asChild><Link to="/skapa-annons">Skapa din första annons</Link></Button>
              </div>
            ) : (
              <div className="space-y-3">
                {listings.map((l) => (
                  <div key={l.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
                    <div>
                      <Link to={`/annonser/${l.id}`} className="font-medium hover:text-primary transition-colors">{l.title}</Link>
                      <p className="text-sm text-muted-foreground">{l.category} • {l.location}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/annonser/${l.id}/redigera`}><Pencil className="h-3.5 w-3.5" /></Link>
                      </Button>
                      <Badge variant={l.status === "aktiv" ? "default" : "secondary"}>{l.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Provider dashboard */}
          <TabsContent value="provider">
            {!providerProfile ? (
              <div className="rounded-lg border border-border bg-card p-8 text-center">
                <p className="mb-2 text-muted-foreground">Du har inte skapat en leverantörsprofil ännu.</p>
                <Button asChild><Link to="/skapa-leverantorsprofil">Skapa profil</Link></Button>
              </div>
            ) : (
              <>
                <div className="mb-6 rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{providerProfile.business_name}</h3>
                      <p className="text-sm text-muted-foreground">{providerProfile.business_type === "företag" ? "Företag" : "Hobbyaktör"} • {providerProfile.service_area}</p>
                    </div>
                    {providerProfile.verified && profile?.admin_approved && <Badge>Verifierat företag</Badge>}
                  </div>
                </div>

                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-sans text-lg font-semibold">Inkomna bokningar</h2>
                  <Button size="sm" asChild><Link to="/skapa-tjanst"><Plus className="mr-2 h-4 w-4" /> Ny tjänst</Link></Button>
                </div>

                {providerBookings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Inga bokningar ännu.</p>
                ) : (
                  <div className="space-y-3">
                    {providerBookings.map((b) => (
                      <div key={b.id} className="rounded-lg border border-border bg-card p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{(b as any).services?.title || "Tjänst"}</p>
                            <p className="text-sm text-muted-foreground">
                              Kund: {(b as any).profiles?.full_name || "Anonym"} • {b.scheduled_date || "Inget datum"}
                            </p>
                            {b.notes && <p className="mt-1 text-sm text-muted-foreground">{b.notes}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[b.status] || "bg-muted"}`}>
                              {statusLabels[b.status] || b.status}
                            </span>
                          </div>
                        </div>
                        {b.status === "requested" && (
                          <div className="mt-3 flex gap-2">
                            <Button size="sm" onClick={() => handleBookingAction(b.id, "accepted")}>Acceptera</Button>
                            <Button size="sm" variant="outline" onClick={() => handleBookingAction(b.id, "declined")}>Neka</Button>
                          </div>
                        )}
                        {b.status === "accepted" && (
                          <Button size="sm" className="mt-3" onClick={() => handleBookingAction(b.id, "scheduled")}>Markera schemalagd</Button>
                        )}
                        {b.status === "scheduled" && (
                          <Button size="sm" className="mt-3" onClick={() => handleBookingAction(b.id, "in_progress")}>Starta arbete</Button>
                        )}
                        {b.status === "in_progress" && (
                          <Button size="sm" className="mt-3" onClick={() => handleBookingAction(b.id, "completed")}>Markera slutförd</Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Purchase requests */}
          <TabsContent value="purchases">
            <PurchaseRequestsTab />
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages">
            <MessagesTab />
          </TabsContent>
        </Tabs>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}

function extractIdsFromThread(threadId: string, currentUserId: string): { listingId: string | null; otherUserId: string | null; isDirect: boolean } {
  // Direct (no listing): direct-{userIdA}-{userIdB}
  const directMatch = threadId.match(/^direct-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/);
  if (directMatch) {
    const [, a, b] = directMatch;
    const otherUserId = a === currentUserId ? b : a;
    return { listingId: null, otherUserId, isDirect: true };
  }
  const match = threadId.match(/^listing-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/);
  if (!match) return { listingId: null, otherUserId: null, isDirect: false };
  const listingId = match[1];
  const trailingUserId = match[2];
  // The trailing user is either the current user or the other party.
  // We also need the seller_id from the listing to determine the other party.
  return { listingId, otherUserId: trailingUserId === currentUserId ? null : trailingUserId, isDirect: false };
}

function MessagesTab() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<{ thread_id: string; last_message: string; last_at: string }[]>([]);
  const [listingMap, setListingMap] = useState<Record<string, { title: string; seller_id: string; images: string[] | null }>>({});
  const [profileMap, setProfileMap] = useState<Record<string, { full_name: string; avatar_url: string | null; username: string | null }>>({});
  const [emailMap, setEmailMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data }, { data: deletions }] = await Promise.all([
        supabase
          .from("messages")
          .select("thread_id, content, created_at")
          .order("created_at", { ascending: false }),
        supabase
          .from("thread_deletions")
          .select("thread_id, deleted_at")
          .eq("user_id", user.id),
      ]);
      if (!data) return;
      const deletedAtMap = new Map<string, string>();
      (deletions || []).forEach((d: any) => deletedAtMap.set(d.thread_id, d.deleted_at));

      const seen = new Map<string, { last_message: string; last_at: string }>();
      data.forEach((m: any) => {
        if (!seen.has(m.thread_id)) {
          seen.set(m.thread_id, { last_message: m.content, last_at: m.created_at });
        }
      });
      // Hide threads whose latest message is older than (or equal to) when this user deleted them
      const threadList = Array.from(seen.entries())
        .map(([thread_id, rest]) => ({ thread_id, ...rest }))
        .filter(t => {
          const del = deletedAtMap.get(t.thread_id);
          if (!del) return true;
          return new Date(t.last_at) > new Date(del);
        });
      setThreads(threadList);

        // Extract listing IDs
        const listingIds = new Set<string>();
        const userIds = new Set<string>();
        threadList.forEach(t => {
          const { listingId, otherUserId } = extractIdsFromThread(t.thread_id, user.id);
          if (listingId) listingIds.add(listingId);
          if (otherUserId) userIds.add(otherUserId);
        });

        // Fetch listings
        if (listingIds.size > 0) {
          supabase
            .from("listings")
            .select("id, title, seller_id, images")
            .in("id", Array.from(listingIds))
            .then(({ data: listings }) => {
              if (listings) {
                const map: Record<string, { title: string; seller_id: string; images: string[] | null }> = {};
                listings.forEach((l: any) => { map[l.id] = { title: l.title, seller_id: l.seller_id, images: l.images }; });
                setListingMap(map);

                // Now figure out all other user IDs (including seller_ids)
                threadList.forEach(t => {
                  const { listingId, otherUserId } = extractIdsFromThread(t.thread_id, user.id);
                  if (listingId && map[listingId]) {
                    if (map[listingId].seller_id === user.id) {
                      // Current user is seller, other user is the trailing ID
                      if (otherUserId) userIds.add(otherUserId);
                    } else {
                      // Current user is buyer, other user is the seller
                      userIds.add(map[listingId].seller_id);
                    }
                  }
                });

                // Fetch profiles
                if (userIds.size > 0) {
                  supabase
                    .from("profiles")
                    .select("id, full_name, avatar_url, username")
                    .in("id", Array.from(userIds))
                    .then(({ data: profiles }) => {
                      if (profiles) {
                        const pMap: Record<string, { full_name: string; avatar_url: string | null; username: string | null }> = {};
                        profiles.forEach((p: any) => { pMap[p.id] = { full_name: p.full_name, avatar_url: p.avatar_url, username: p.username }; });
                        setProfileMap(pMap);
                      }
                    });
                }
              }
            });
        }
    })();
  }, [user]);

  const getOtherUserId = (threadId: string): string | null => {
    if (!user) return null;
    const { listingId, otherUserId } = extractIdsFromThread(threadId, user.id);
    if (!listingId || !listingMap[listingId]) return otherUserId;
    if (listingMap[listingId].seller_id === user.id) {
      return otherUserId;
    }
    return listingMap[listingId].seller_id;
  };

  const [typeFilter, setTypeFilter] = useState<"all" | "listing" | "direct">("all");
  const [userFilter, setUserFilter] = useState<string>("all");

  if (threads.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-muted-foreground">Inga meddelanden ännu.</p>
      </div>
    );
  }

  // Build user list (only users that appear in current threads)
  const userOptions = new Map<string, string>();
  threads.forEach(t => {
    const otherUserId = getOtherUserId(t.thread_id);
    if (otherUserId) {
      const name = profileMap[otherUserId]?.full_name || "Okänd användare";
      userOptions.set(otherUserId, name);
    }
  });

  const filteredThreads = threads.filter(t => {
    const { isDirect } = extractIdsFromThread(t.thread_id, user?.id || "");
    if (typeFilter === "listing" && isDirect) return false;
    if (typeFilter === "direct" && !isDirect) return false;
    if (userFilter !== "all") {
      const otherUserId = getOtherUserId(t.thread_id);
      if (otherUserId !== userFilter) return false;
    }
    return true;
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Typ av chatt" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla chattar</SelectItem>
            <SelectItem value="listing">Annons-chattar</SelectItem>
            <SelectItem value="direct">Personliga chattar</SelectItem>
          </SelectContent>
        </Select>

        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-full sm:w-[240px]">
            <SelectValue placeholder="Filtrera efter användare" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla användare</SelectItem>
            {Array.from(userOptions.entries()).map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filteredThreads.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-muted-foreground">Inga chattar matchar filtret.</p>
        </div>
      ) : (
        filteredThreads.map(t => {
          const { listingId } = extractIdsFromThread(t.thread_id, user?.id || "");
          const listing = listingId ? listingMap[listingId] : null;
          const otherUserId = getOtherUserId(t.thread_id);
          const otherProfile = otherUserId ? profileMap[otherUserId] : null;

          return (
            <div
              key={t.thread_id}
              className="flex items-center justify-between rounded-lg border border-border bg-card p-4 transition-colors hover:bg-secondary"
            >
              <Link
                to={`/meddelanden/${t.thread_id}`}
                className="min-w-0 flex-1"
              >
                <p className="font-medium text-sm">{listing?.title || (extractIdsFromThread(t.thread_id, user?.id || "").isDirect ? "Direktmeddelande" : "Konversation")}</p>
                <p className="text-sm text-muted-foreground line-clamp-1">{t.last_message}</p>
                <p className="text-xs text-muted-foreground mt-1">{new Date(t.last_at).toLocaleString("sv-SE")}</p>
              </Link>

              <div className="ml-4 flex flex-shrink-0 items-center gap-2">
                {listing && listingId && (
                  <Link
                    to={`/annonser/${listingId}`}
                    className="hidden sm:flex items-center gap-3 rounded-lg border border-border bg-secondary/50 px-3 py-2 transition-colors hover:bg-secondary max-w-[220px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {listing.images && listing.images[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="h-9 w-9 rounded object-cover" />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded bg-primary/10 text-xs font-semibold text-primary">
                        AN
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Annons</p>
                      <p className="text-sm font-medium truncate">{listing.title}</p>
                    </div>
                  </Link>
                )}

                {otherProfile && otherUserId && (
                  <Link
                    to={`/saljare/${otherProfile.username || otherUserId}`}
                    className="flex items-center gap-3 rounded-lg border border-border bg-secondary/50 px-3 py-2 transition-colors hover:bg-secondary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {otherProfile.avatar_url ? (
                      <img src={otherProfile.avatar_url} alt={otherProfile.full_name} className="h-9 w-9 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                        {otherProfile.full_name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Användare</p>
                      <p className="text-sm font-medium truncate">{otherProfile.full_name}</p>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
